import { Link, NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import cpApi from "@/lib/clientPortalApi";
import { useCpAuth } from "@/contexts/ClientPortalAuthContext";
import {
  LayoutDashboard, TrendingUp, ShoppingCart, RefreshCw, AlertCircle,
  FileX2, HelpCircle, Receipt, CreditCard, CalendarCheck2, FileQuestion,
  LogOut, Loader2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronsUpDown, Check } from "lucide-react";

function CompanyPicker({ companies, activeId, onPick }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="mt-2 w-full text-[10px] tracking-wide uppercase text-[#0a2540] bg-white border border-slate-200 rounded-sm px-2 py-1.5 flex items-center justify-between hover:border-[#0a2540] gap-1"
          data-testid="cp-company-switcher"
        >
          <span className="truncate text-left flex-1">Switch company</span>
          <ChevronsUpDown size={11} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 max-h-72 overflow-y-auto">
        <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase">Your companies</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.client_id}
            onClick={() => c.client_id !== activeId && onPick(c.client_id)}
            className="text-xs cursor-pointer"
            data-testid={`cp-switch-company-${c.client_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.client_name}</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-wide">
                {c.role === "company_admin" ? "Company Admin" : "Employee"}
              </div>
            </div>
            {c.client_id === activeId && <Check size={12} className="ml-2 text-[#0a2540]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// section_key, label, icon. Dashboard is always visible.
const NAV_ITEMS = [
  { section: null, to: "/client-portal/dashboard", label: "Overview", icon: LayoutDashboard },
  { section: "sales_b2b", to: "/client-portal/sections/sales_b2b", label: "Sales B2B", icon: TrendingUp },
  { section: "sales_b2c", to: "/client-portal/sections/sales_b2c", label: "Sales B2C", icon: ShoppingCart },
  { section: "sales_returns", to: "/client-portal/sections/sales_returns", label: "Sales Return", icon: RefreshCw },
  { section: "missing_invoices", to: "/client-portal/sections/missing_invoices", label: "Missing Invoices", icon: AlertCircle },
  { section: "gstr1_unfiled", to: "/client-portal/sections/gstr1_unfiled", label: "GSTR-1 Unfiled", icon: FileX2 },
  { section: "suspense", to: "/client-portal/sections/suspense", label: "Suspense Account", icon: HelpCircle },
  { section: "receivables", to: "/client-portal/sections/receivables", label: "Receivables", icon: Receipt },
  { section: "payables", to: "/client-portal/sections/payables", label: "Payables", icon: CreditCard },
  { section: "compliance", to: "/client-portal/compliance", label: "Compliance", icon: CalendarCheck2 },
  { section: "queries", to: "/client-portal/queries", label: "Queries / RFIs", icon: FileQuestion },
];

export default function CpLayout() {
  const { user, client, loading, logout, companies, switchCompany } = useCpAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(null); // array of section keys, null=loading

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      if (!user) {
        if (active) setVisible([]);
        return;
      }
      try {
        const r = await cpApi.get("/client-portal/dashboard");
        if (active) setVisible(r.data.visible_sections || []);
      } catch (e) {
        if (active) setVisible([]);
      }
    };
    fetch();
    return () => { active = false; };
  }, [user]);

  if (loading || (user && visible === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-[#0a2540]" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/client-portal/login" replace state={{ from: location }} />;
  if (user.must_change_password && !location.pathname.endsWith("/change-password")) {
    return <Navigate to="/client-portal/change-password" replace />;
  }

  const visibleSet = new Set(visible || []);
  const isEmployee = user.role !== "company_admin";
  const visibleNav = NAV_ITEMS
    // Employees (role === "employee") do not get the Overview dashboard —
    // the aggregated tax/sales metrics are admin-only.
    .filter((n) => !(isEmployee && n.section == null))
    .filter((n) => n.section == null || visibleSet.has(n.section));

  return (
    <div className="min-h-screen flex bg-white">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-slate-200 bg-[#f8f9fa]" data-testid="cp-sidebar">
        <div className="px-5 py-5 border-b border-slate-200 flex items-start gap-2.5">
          <img src="/branding/griffin-logo.png" alt="Griffin" className="h-9 w-9 rounded-sm bg-white object-contain shrink-0" />
          <div className="leading-tight min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight break-words" style={{ fontFamily: "Outfit" }}>{client?.name || "Client Portal"}</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 break-words">Griffin Pulse · Client Portal</div>
            {companies && companies.length > 1 && (
              <CompanyPicker
                companies={companies}
                activeId={client?.id}
                onPick={switchCompany}
              />
            )}
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {visibleNav.map(({ to, label, icon: Icon, section }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`cp-nav-${section || "overview"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-[#0a2540] text-white" : "text-slate-700 hover:bg-white hover:text-[#0a2540]"
                }`
              }
            >
              <Icon size={16} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-2 py-2 hover:bg-white rounded-sm" data-testid="cp-user-menu">
                <div className="h-8 w-8 bg-[#0a2540] text-white text-sm rounded-full flex items-center justify-center">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{user.name}</div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                    {user.role === "company_admin" ? "Company Admin" : "Employee"}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/client-portal/change-password" data-testid="cp-menu-change-pw">Change password</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="cp-menu-logout">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-30 min-h-12 bg-white border-b border-slate-200 flex items-center px-3 gap-2 py-1.5">
        <img src="/branding/griffin-logo.png" alt="Griffin" className="h-6 w-6 shrink-0" />
        <div className="text-sm font-semibold break-words leading-tight" style={{ fontFamily: "Outfit" }}>{client?.name || "Client Portal"}</div>
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden pt-12 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
